import { useCurrentAccount, useUnseenMessages } from '@application/api';
import {
  MtApplicationsBuyerPayModalController,
  MtNotificationsPanelController,
  SeoController,
  UnfinishedApplicationController,
} from '@application/components';
import { useLanguageSelectorButtons, useRouter } from '@application/utils';
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import type { MtLayoutData, MtLayoutProps } from '@presentation/components';
import { LoadingOverlay, MT_LAYOUT_CONTENT_WRAPPER_ID, MtLayout } from '@presentation/components';
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavbarLinkCategoriesBreeder } from './links/breeder';
import { useNavbarLinkCategoriesBuyer } from './links/buyer';
import { useBreederAlertBar } from './useBreederAlertBar';
import { useBuyerAlertBar } from './useBuyerAlertBar';
import { useCurrentProfileStep } from '../pages/MtBreederOnboardingPageController/api/useCurrentProfileStep';

export const MtLayoutController: FC<{ children: ReactNode }> = (props) => {
  const { children } = props;
  const { navigateToRoute, translate, infoFromCurrentPath } = useRouter();
  const {
    currentAccountType,
    currentAccountLoggedIn,
    currentAccount,
    currentAccountLoading,
    currentAccountLogout,
  } = useCurrentAccount();

  const navbarLinkCategoriesBreeder = useNavbarLinkCategoriesBreeder();
  const navbarLinkCategoriesBuyer = useNavbarLinkCategoriesBuyer();
  const { languageSelectorButtons } = useLanguageSelectorButtons();
  const { profileLocked, profileValidated, currentProfileProgress, profileLoading } =
    useCurrentProfileStep();

  const { unseenMessagesAmount } = useUnseenMessages();
  const { alertBar: buyerAlertBar, checkoutApplicationModalProps } = useBuyerAlertBar({
    currentAccountType,
  });

  const { alertBar: breederAlertBar } = useBreederAlertBar({
    currentAccountType,
  });

  const homeClickHandler: MtLayoutProps['onHomeClick'] = useCallback(
    () => navigateToRoute('homeLanding'),
    [navigateToRoute],
  );

  const dropdownLinks = useMemo((): MtLayoutData['dropdownLinks'] => {
    return [
      ...(currentAccountType === 'breeder'
        ? [
            {
              label: translate('mt.header.breederProfile'),
              icon: profileLocked ? 'lock' : ('user' as IconName),
              disabled: profileLocked,
              onClick: () => navigateToRoute('mtBreederProfile'),
            },
          ]
        : []),
      ...(currentAccountType === 'buyer' || (currentAccountType === 'breeder' && profileValidated)
        ? [
            {
              label: translate('mt.header.messages'),
              icon: 'comments' as IconName,
              onClick: () => navigateToRoute('mtMessages'),
              notifications: unseenMessagesAmount,
            },
          ]
        : []),
      {
        label: translate('mt.header.settings'),
        icon: 'cog' as IconName,
        onClick: () => navigateToRoute('mtSettings'),
      },
      {
        label: translate('mt.header.logout'),
        icon: 'sign-out-alt' as IconName,
        onClick: () => currentAccountLogout(),
      },
    ];
  }, [
    currentAccountLogout,
    currentAccountType,
    navigateToRoute,
    profileLocked,
    profileValidated,
    translate,
    unseenMessagesAmount,
  ]);

  const formattedUser = useMemo((): MtLayoutData['user'] => {
    const getName = () => {
      if (!currentAccount) return '';
      const { firstName, lastName, name } = currentAccount;
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      }
      return name ?? '';
    };
    const image = (currentAccount?.imgs ?? [])?.[0]?.small ?? undefined;
    return {
      name: getName(),
      image,
    };
  }, [currentAccount]);

  useEffect(() => {
    const el = document.getElementById(MT_LAYOUT_CONTENT_WRAPPER_ID);
    if (el) {
      el.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [infoFromCurrentPath]);

  if (!currentAccountLoggedIn) {
    if (currentAccountLoading) return <LoadingOverlay isLoading />;
    if (!currentAccount) navigateToRoute('login');
  }

  return (
    <>
      <SeoController title={translate('mt.seo.title')} noIndex />
      <MtLayout
        user={formattedUser}
        dropdownLinks={dropdownLinks}
        navbarLinkCategories={
          currentAccountType === 'breeder' ? navbarLinkCategoriesBreeder : navbarLinkCategoriesBuyer
        }
        onHomeClick={homeClickHandler}
        onLogoutClick={currentAccountLogout}
        alertBar={buyerAlertBar || breederAlertBar}
        notificationsPanel={<MtNotificationsPanelController />}
        unfinishedApplicationComponent={<UnfinishedApplicationController />}
        profileCurrentProgress={
          currentAccountType === 'breeder' && !profileLoading ? currentProfileProgress : undefined
        }
        languageSelectorButtons={languageSelectorButtons}
      >
        {children}
      </MtLayout>
      <MtApplicationsBuyerPayModalController {...checkoutApplicationModalProps} />
    </>
  );
};
